import * as React from "react";
import { Build, Job } from "~/components/build/Show/lib/types";
import Database from "~/lib/Database";
import { LimitingJob } from "~/components/job/Job/shared/types";
import { JobRow } from "~/components/job/Job/shared/JobRow";
import { LimitingBanner } from "~/components/job/Job/shared/LimitingBanner";
import { useLimitingJobsSubscription } from "~/components/job/Job/shared/useLimitingJobsSubscription";

// If this number of limiting jobs is returned, assume there may be more that were not returned
// To change this limit, first update the value in Job::PlatformLimitingJobsController
const PLATFORM_LIMITING_JOBS_LIMIT = 30;

export function LimitedByPlatform({ build, job }: { build: Build; job: Job }) {
  const [limitingJobs, setLimitingJobs] = React.useState<Array<LimitingJob | null>>([]);
  const [concurrentJobLimit, setConcurrentJobLimit] = React.useState<number | null>(null);
  const [userCanChangeBilling, setUserCanChangeBilling] = React.useState<boolean>(false);

  const getLimitingJobs = React.useCallback(() => {
    fetch(job.platformLimitingJobsPath)
      .then((response) => response.json())
      .then((data) => {
        const parsedData = Database.parse(data);
        if (Array.isArray(parsedData)) {
          // Handle legacy response format (array of jobs)
          setLimitingJobs(parsedData);
          setConcurrentJobLimit(null);
          setUserCanChangeBilling(false);
        } else {
          // Handle new response format (object with jobs, limit, and billing permission)
          setLimitingJobs(parsedData.jobs || []);
          setConcurrentJobLimit(parsedData.concurrentJobLimit);
          setUserCanChangeBilling(parsedData.userCanChangeBilling || false);
        }
      });
  }, [build.account.slug, build.project.slug, build.id, job.id]);

  React.useEffect(() => {
    // Only fetch limiting jobs if platform limits may apply to this organization
    if (job.platformLimitedMayApply) {
      getLimitingJobs();
    }
  }, [getLimitingJobs, job.platformLimitedMayApply]);

  // Don't render if platform limits don't apply to this organization
  if (!job.platformLimitedMayApply) {
    return null;
  }

  useLimitingJobsSubscription(limitingJobs, getLimitingJobs, ["platform_limited"]);

  if (limitingJobs.length === 0) {
    return null;
  }

  return (
    <LimitingBanner colorScheme="blue">
      <h3 className="mb-2 mt-0 text-base">Concurrent job limit reached</h3>

      <p className="mb-3 mt-0">
        This job is waiting due to limits on your current plan
        {concurrentJobLimit && ` (${concurrentJobLimit} concurrent jobs)`}. The following jobs are
        using platform concurrency or are ahead in the queue:
      </p>

      <ul className="depth mb-3 max-w-3xl divide-y rounded-sm">
        {limitingJobs.map((job, index) => (
          <JobRow key={job?.uuid || index} job={job} />
        ))}

        {limitingJobs.length >= PLATFORM_LIMITING_JOBS_LIMIT && (
          <li key={null} className="monospace m-0 border-gray-300 p-2.5 text-xs">
            Displaying only the first {limitingJobs.length} jobs
          </li>
        )}
      </ul>

      <p className="m-0">
        {userCanChangeBilling ? (
          <>
            <a
              href={job.platformProUpgradePath}
              className="text-inherit underline"
              target="_blank"
              rel="noreferrer"
            >
              Upgrade to Platform Pro
            </a>{" "}
            to remove concurrent job limits.
          </>
        ) : (
          "Contact your organization admin to upgrade to Platform Pro to remove concurrent job limits."
        )}
      </p>
    </LimitingBanner>
  );
}
